// ============================================================
// useFinder — 予約 + 車両コード検索ロジック
// ============================================================

import { useState, useCallback, useMemo } from 'react';
import { Reservation, Vehicle } from './types';

interface UseFinderProps {
  reservations: Reservation[];
  vehicles: Vehicle[];
}

export interface FinderResult {
  type: 'reservation' | 'vehicle';
  reservation?: Reservation;
  vehicle?: Vehicle;
  vehicleIndex?: number;
}

interface UseFinderReturn {
  query: string;
  setQuery: (q: string) => void;
  results: FinderResult[];
  currentIndex: number;
  /** 後方互換: 現在の予約結果（ハイライト用） */
  currentReservation: Reservation | null;
  /** 現在の車両結果（スクロール用） */
  currentVehicle: { vehicle: Vehicle; index: number } | null;
  search: () => void;
  next: () => void;
  prev: () => void;
  clear: () => void;
}

export function useFinder({ reservations, vehicles }: UseFinderProps): UseFinderReturn {
  const [query, setQueryState] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const out: FinderResult[] = [];

    // 車両コード検索（vehicle_code, vehicle_number）
    vehicles.forEach((v, idx) => {
      if (
        (v.vehicle_code || '').toLowerCase().includes(q) ||
        (v.vehicle_number || '').toLowerCase().includes(q)
      ) {
        out.push({ type: 'vehicle', vehicle: v, vehicleIndex: idx });
      }
    });

    // 予約検索（顧客名、予約コード）
    reservations.forEach(r => {
      if (
        (r.customer_name || '').toLowerCase().includes(q) ||
        (r.contact_code || '').toLowerCase().includes(q)
      ) {
        out.push({ type: 'reservation', reservation: r });
      }
    });

    return out;
  }, [reservations, vehicles, query]);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    setCurrentIndex(0);
  }, []);

  const search = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  const next = useCallback(() => {
    if (results.length === 0) return;
    setCurrentIndex(i => (i + 1) % results.length);
  }, [results.length]);

  const prev = useCallback(() => {
    if (results.length === 0) return;
    setCurrentIndex(i => (i - 1 + results.length) % results.length);
  }, [results.length]);

  const clear = useCallback(() => {
    setQueryState('');
    setCurrentIndex(0);
  }, []);

  const current = results[currentIndex] ?? null;
  const currentReservation = current?.type === 'reservation' ? current.reservation! : null;
  const currentVehicle = current?.type === 'vehicle'
    ? { vehicle: current.vehicle!, index: current.vehicleIndex! }
    : null;

  return {
    query, setQuery, results, currentIndex,
    currentReservation, currentVehicle,
    search, next, prev, clear,
  };
}
